import React, { useState, useEffect, useContext } from 'react';
import { Table, Card, Tag, Spin, Alert, Button, Typography, Space, Divider } from 'antd';
import { 
  HistoryOutlined, 
  DollarOutlined, 
  CalendarOutlined, 
  CreditCardOutlined,
  LoadingOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { BASE_URL } from '../../Constants';
import { EditFormContext } from './PublicEmployeeEditForm';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

const TransactionHistory = () => {
  const { t } = useTranslation();
  const { formData } = useContext(EditFormContext);
  const employeeId = formData?.id;
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transactionCount, setTransactionCount] = useState(0);

  const fetchTransactions = async () => {
    if (!employeeId) {
      setError('Employee ID is required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `${BASE_URL}/Payment/employee/${employeeId}/transactions`
      );
      
      if (response.status === 200 && response.data) {
        setTransactions(response.data.transactions || []);
        setTransactionCount(response.data.transactionCount || 0);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(t('transactionHistory.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [employeeId]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'green';
      case 'pending':
        return 'orange';
      case 'failed':
      case 'error':
        return 'red';
      case 'cancelled':
        return 'gray';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return t('transactionHistory.completed');
      case 'success':
        return t('transactionHistory.success');
      case 'pending':
        return t('transactionHistory.pending');
      case 'failed':
        return t('transactionHistory.failed');
      case 'error':
        return t('transactionHistory.error');
      case 'cancelled':
        return t('transactionHistory.cancelled');
      default:
        return status || t('transactionHistory.unknown');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('hardcodedTexts.nA');
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return t('transactionHistory.invalidDate');
    }
  };

  const columns = [
    {
      title: t('transactionHistory.transactionReference'),
      dataIndex: 'transactionReference',
      key: 'transactionReference',
      width: 180,
      render: (text) => (
        <Text code copyable={{ text }}>
          {text}
        </Text>
      ),
    },
    {
      title: t('transactionHistory.amount'),
      dataIndex: 'planAmount',
      key: 'planAmount',
      width: 150,
      render: (planAmount, record) => {
        const finalAmount = record.finalAmount || planAmount;
        const hasCoupon = record.couponId && finalAmount < planAmount;
        const discount = planAmount - finalAmount;
        
        return (
          <div className="space-y-1">
            {hasCoupon ? (
              <>
                <div className="flex items-center">
                  <Text delete className="text-gray-400 text-xs">
                    {planAmount} {record.currency || 'SAR'}
                  </Text>
                </div>
                <div className="flex items-center">
                  <DollarOutlined className="mr-1 text-green-600" />
                  <Text strong className="text-green-600">
                    {finalAmount} {record.currency || 'SAR'}
                  </Text>
                </div>
                <div className="text-xs text-green-600">
                  (-{discount.toFixed(2)} {record.currency || 'SAR'})
                </div>
              </>
            ) : (
              <div className="flex items-center">
                <DollarOutlined className="mr-1 text-green-600" />
                <Text strong className="text-green-600">
                  {planAmount} {record.currency || 'SAR'}
                </Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('transactionHistory.plan'),
      dataIndex: 'planId',
      key: 'planId',
      width: 100,
      render: (planId) => (
        <Tag color="blue">
          {planId === 'monthly' ? t('transactionHistory.monthly') : planId === 'yearly' ? t('transactionHistory.yearly') : planId}
        </Tag>
      ),
    },
    {
      title: t('transactionHistory.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: t('transactionHistory.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date) => (
        <div className="flex items-center">
          <CalendarOutlined className="mr-1 text-gray-500" />
          <Text>{formatDate(date)}</Text>
        </div>
      ),
    },
    {
      title: t('transactionHistory.customerDetails'),
      key: 'customerDetails',
      width: 200,
      render: (_, record) => (
        <div className="space-y-1">
          <div className="text-sm font-medium">{record.customerDetails?.name}</div>
          <div className="text-xs text-gray-500">{record.customerDetails?.email}</div>
          <div className="text-xs text-gray-500">{record.customerDetails?.phone}</div>
        </div>
      ),
    },
  ];

  const LoadingComponent = () => (
    <div className="flex flex-col items-center justify-center py-20">
      <Spin 
        size="large" 
        indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />}
      />
      <div className="mt-6 text-center">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">
          {t('transactionHistory.loadingTitle')}
        </h3>
        <p className="text-gray-500">
          {t('transactionHistory.loadingDescription')}
        </p>
      </div>
    </div>
  );

  if (loading) {
    return <LoadingComponent />;
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <HistoryOutlined className="text-blue-600 text-xl" />
          </div>
          <div>
            <Title level={4} className="mb-0">
              {t('transactionHistory.title')}
            </Title>
            <Text type="secondary">
              {t('transactionHistory.description')}
            </Text>
          </div>
        </div>
        <Button 
          icon={<ReloadOutlined />} 
          onClick={fetchTransactions}
          loading={loading}
        >
          {t('transactionHistory.refresh')}
        </Button>
      </div>

      <Divider />

      {/* Error State */}
      {error && (
        <Alert
          message={t('dashboard.errorLoadingTransactions')}
          description={error}
          type="error"
          action={
            <Button size="small" danger onClick={fetchTransactions}>
              {t('transactionHistory.tryAgain')}
            </Button>
          }
          className="mb-4"
        />
      )}

      {/* Transactions Table */}
      {!error && (
        <Card>
          <Table
            columns={columns}
            dataSource={transactions}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} transactions`,
            }}
            scroll={{ x: 1000 }}
            locale={{
              emptyText: (
                <div className="py-8 text-center">
                  <HistoryOutlined className="text-4xl text-gray-300 mb-2" />
                  <p className="text-gray-500">{t('transactionHistory.noTransactions')}</p>
                  <p className="text-sm text-gray-400">
                    {t('transactionHistory.noTransactionsDescription')}
                  </p>
                </div>
              ),
            }}
          />
        </Card>
      )}
    </div>
  );
};

export default TransactionHistory;
